import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { contactAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Shield, ArrowLeft, Send, CheckCircle } from 'lucide-react';

export default function Contact() {
  const { t, language } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState(''); // Honeypot - bots fill this
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await contactAPI.submit({ name, email, company: company || undefined, message, website_url: websiteUrl });
      setSuccess(true);
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || 'Error')
        : (detail || (language === 'fr'
          ? 'Une erreur est survenue. Vérifiez votre email professionnel.'
          : 'An error occurred. Please use a corporate email.'));
      setError(typeof msg === 'string' ? msg : String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-lg tracking-tight">SheetArmor</span>
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-6 py-16">
        <Link to="/">
          <Button variant="ghost" className="text-neutral-400 hover:text-white mb-8 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
        </Link>

        <div className="mb-12">
          <span className="text-blue-600 text-xs font-body tracking-widest uppercase">
            {t('contact.tag')}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3 mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-neutral-400 font-body text-base leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>

        {success ? (
          <div className="border border-blue-600/30 bg-blue-600/5 rounded-sm p-8 flex flex-col items-center text-center">
            <CheckCircle className="w-12 h-12 text-blue-600 mb-4" strokeWidth={1.5} />
            <h2 className="font-heading text-xl text-white mb-2">{t('contact.success_title')}</h2>
            <p className="text-neutral-400 font-body text-sm leading-relaxed max-w-md">
              {t('contact.success_message')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot - invisible to humans, bots will fill it */}
            <div className="opacity-0 absolute -z-10 w-0 h-0 overflow-hidden" aria-hidden="true">
              <label htmlFor="website_url">Website</label>
              <input
                id="website_url"
                name="website_url"
                type="text"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="name" className="text-neutral-300 font-body text-sm">
                {t('contact.name')}
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-blue-600"
                placeholder={language === 'fr' ? 'Jean Dupont' : 'John Smith'}
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-neutral-300 font-body text-sm">
                {t('contact.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-blue-600"
                placeholder={language === 'fr' ? 'jean@entreprise.fr' : 'john@company.com'}
              />
              <p className="text-neutral-500 text-xs mt-1 font-body">
                {t('contact.email_hint')}
              </p>
            </div>

            <div>
              <Label htmlFor="company" className="text-neutral-300 font-body text-sm">
                {t('contact.company')} <span className="text-neutral-500">({language === 'fr' ? 'optionnel' : 'optional'})</span>
              </Label>
              <Input
                id="company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-blue-600"
                placeholder={language === 'fr' ? 'Nom de votre société' : 'Your company name'}
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-neutral-300 font-body text-sm">
                {t('contact.message')}
              </Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus-visible:ring-blue-600 resize-none"
                placeholder={language === 'fr'
                  ? 'Décrivez votre besoin ou posez votre question...'
                  : 'Describe your needs or ask your question...'}
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm font-body">{error}</p>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-xs font-body tracking-widest rounded-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  {t('contact.submit')}
                </>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
